import { Dialog, Grid, Slider, styled, useTheme } from '@mui/material';
import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import Cropper, { CropperProps } from 'react-easy-crop';
import { PLACEHOLDER_GREEN_IMAGE } from '../../utils/constants';
import { AppButton } from '../AppButton';

const cropWindow = {
  width: 400,
  height: 360,
} as const;

const CropperContainer = styled('div')({
  width: cropWindow.width,
  padding: 25,
  '.crop': {
    position: 'relative',
    width: '100%',
    height: cropWindow.height,
    background: '#fff',
    borderRadius: 10,
    overflow: 'hidden',
  },
  '.control': {
    padding: '15px 0 0 10px',
    display: 'flex',
    alignItems: 'center',
  },
});

const LoadingContainer = styled(Grid, {
  shouldForwardProp: (prop) => !['aspectRatio'].includes(prop.toString()),
})<{ aspectRatio?: CropperProps['aspect'] }>(({ aspectRatio }) => ({
  width: cropWindow.width,
  height: cropWindow.height,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  img: {
    aspectRatio: `${aspectRatio}`,
    overflow: 'hidden',
  },
}));

export interface ImageCropperProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  file: File | null;
  cropperProps: Partial<CropperProps>;
  onFileChange: (newFile: File | null) => void;
}

export const ImageCropper = ({
  open,
  setOpen,
  file,
  fileTypeToUse,
  cropperProps,
  onFileChange,
}: { fileTypeToUse?: string } & ImageCropperProps) => {
  const theme = useTheme();
  const [imageSrc, setImageSrc] = useState<string>('');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [zoom, setZoom] = useState(1);

  const onCropComplete = useCallback((_croppedArea: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const cropIt = useCallback(async () => {
    if (file) {
      try {
        const croppedImage: any = await getCroppedImg(imageSrc, croppedAreaPixels, fileTypeToUse || file?.type);

        const newFileEnding = fileTypeToUse?.split('/')?.[1]; // e.g. 'webp'
        const newFileName = newFileEnding // need to replace old file ending with new file ending
          ? `${file.name.substring(0, file.name.lastIndexOf('.'))}.${newFileEnding}`
          : file.name;

        const newFile = new File([croppedImage], newFileName, {
          type: fileTypeToUse || file?.type,
        });

        onFileChange(newFile);
        setOpen(false);
      } catch (e) {
        console.error('error cropping image: ', e);
      }
    }
  }, [imageSrc, croppedAreaPixels, file, onFileChange, setOpen]);

  //   const moveCrop = (x: number, y: number) => {
  //     setCrop({ x: crop.x + x, y: crop.y + y });
  //   };

  useEffect(() => {
    setCrop({ x: 0, y: 0 });
  }, [imageSrc]);

  useEffect(() => {
    const initImage = async () => {
      const imageDataUrl = await readFile(file);
      setImageSrc(imageDataUrl as string);
    };
    if (file) {
      initImage();
    }
  }, [file]);

  return (
    <Dialog
      open={open}
      onClose={() => {
        setOpen(false);
      }}
      maxWidth="lg"
    >
      <CropperContainer>
        {imageSrc ? (
          <>
            <div
              className="crop"
              onKeyDown={(e) => {
                // eslint-disable-next-line no-console
                console.log(e);
              }}
            >
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                restrictPosition={false}
                showGrid={false}
                {...cropperProps}
                minZoom={0.5}
              />
            </div>
            <div className="control">
              <div style={{ flex: 1, marginRight: 30 }}>
                <Slider
                  value={zoom}
                  sx={(theme) => ({
                    color: theme.palette.primary.main,
                  })}
                  min={0}
                  max={3}
                  step={0.1}
                  aria-labelledby="Zoom"
                  classes={{}}
                  onChange={(e, zoom) => setZoom(zoom as number)}
                />
              </div>
              <div>
                <AppButton
                  invertStyle
                  onClick={cropIt}
                  sx={{
                    fontFamily: 'Poppins',
                    fontSize: '14px',
                    fontWeight: 600,
                    backgroundColor: theme.palette.primary.main,
                    border: `1px solid ${theme.palette.primary.main}`,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.main,
                    },
                  }}
                >
                  Crop
                </AppButton>
              </div>
            </div>
          </>
        ) : (
          <LoadingContainer aspectRatio={cropperProps.aspect}>
            <img src={PLACEHOLDER_GREEN_IMAGE['1X1']} alt="loading-placeholder"></img>
          </LoadingContainer>
        )}
      </CropperContainer>
    </Dialog>
  );
};

const readFile = (file: any) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => resolve(reader.result as string), false);
    reader.readAsDataURL(file);
  });
};

const createImage = (url: string) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous'); // needed to avoid cross-origin issues on CodeSandbox
    image.src = url;
  });

const getCroppedImg = async (imageSrc: string, pixelCrop: any, type: string) => {
  const image: any = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    return null;
  }

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.fillStyle = '#ffffff00';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height,
  );

  // As a blob
  return new Promise((resolve) => {
    canvas.toBlob((file) => {
      resolve(file);
    }, type);
  });
};
